import _ from 'lodash';

export const formatDate = date => {
  const y = date.getFullYear();
  const m = `0${date.getMonth() + 1}`.slice(-2);
  const d = `0${date.getDate()}`.slice(-2);
  return `${y}-${m}-${d}`;
};

/**
 * Recursively checks if an object or array contains any key that includes a specific substring.
 * It works for objects, arrays of objects, and deeply nested structures.
 *
 * @param {Object|Array} obj - The object or array to search through.
 * @param {string} substring - The substring to look for within the keys.
 * @returns {boolean} Returns `true` if any key contains the substring; otherwise, `false`.
 */

export const containsKeyWithSubstring = (obj, substring) => {
  try {
    // If the input is an array, iterate over its elements
    if (_.isArray(obj)) {
      return _.some(obj, item => {
        // Recursively check each item in the array
        if (_.isPlainObject(item) || _.isArray(item)) {
          return containsKeyWithSubstring(item, substring);
        }
        return false;
      });
    }

    // If the input is a plain object, iterate over its properties
    if (_.isPlainObject(obj)) {
      return _.some(obj, (value, key) => {
        // Check if the current key includes the specified substring
        if (key.includes(substring)) {
          return true;
        }

        // If the value is an array, check each element recursively
        if (_.isArray(value)) {
          return _.some(value, item => {
            // Ensure the item is an object or array before recursion
            if (_.isPlainObject(item) || _.isArray(item)) {
              return containsKeyWithSubstring(item, substring);
            }
            return false;
          });
        }

        // If the value is a plain object, check its properties recursively
        if (_.isPlainObject(value)) {
          return containsKeyWithSubstring(value, substring);
        }

        // For other data types (e.g., strings, numbers), return false
        return false;
      });
    }

    // Return false if the input is neither an array nor an object
    return false;
  } catch (error) {
    console.log(`containsKeyWithSubstring: ${error?.message} ${JSON.stringify(obj, 2, null)}`);
    return false;
  }
};

/**
 * Recursively searches through an object or array and returns the object that contains
 * a key including the specified substring. If multiple matches are found, the first is returned.
 *
 * @param {Object|Array} obj - The object or array to search through.
 * @param {string} substring - The substring to look for within the keys.
 * @returns {Object|null} Returns the object containing the matching key, or `null` if not found.
 */
export const findObjectWithKeyContainingSubstring = (obj, substring) => {
  // If the input is an array, recursively check each item using _.find
  if (_.isArray(obj)) {
    return _.find(obj, item => {
      if (_.isPlainObject(item) || _.isArray(item)) {
        return findObjectWithKeyContainingSubstring(item, substring);
      }
      return false;
    });
  }

  // If the input is a plain object, check its keys and values
  if (_.isPlainObject(obj)) {
    return (
      _.find(obj, (value, key) => {
        // If a key contains the substring, return the object
        if (key.includes(substring)) {
          return obj;
        }

        // Recursively check nested arrays or objects
        if (_.isArray(value) || _.isPlainObject(value)) {
          return findObjectWithKeyContainingSubstring(value, substring);
        }

        return false;
      }) || null
    );
  }

  // Return null if no match is found
  return null;
};
